<template>
  <main class="login-page">
    <div class="login-shortcuts">
      <button
        type="button"
        :aria-label="t('mine.contactUs')"
        @click="router.push('/mine/about')"
      >
        <AppSvgIcon name="customer" size="32" />
      </button>
      <button
        type="button"
        :aria-label="t('mine.language')"
        @click="showLanguagePopup = true"
      >
        <AppSvgIcon name="language" size="32" />
      </button>
    </div>

    <section class="login-hero">
      <app-image :src="themeStore.image('img1', img1)" alt="" />
      <h1>{{ t("auth.loginTitle") }}</h1>
      <p>{{ t("auth.loginSubtitle") }}</p>
    </section>

    <section class="login-actions">
      <AuthThemeButton block @click="goCreateOption('/auth/email', 'new')">
        {{ t("auth.createWallet") }}
      </AuthThemeButton>

      <AuthThemeButton
        block
        variant="secondary"
        @click="goExistingOption('/auth/email', 'login')"
      >
        {{ t("auth.importWallet") }}
      </AuthThemeButton>
    </section>

    <section
      class="agreement-row"
      :class="{ 'agreement-row--shaking': agreementShaking }"
    >
      <button type="button" class="agreement-check" @click="agreed = !agreed">
        <AppSvgIcon
          name="icon"
          size="16"
          :color="agreed ? 'var(--wp-color-theme)' : ''"
        />
      </button>
      <p class="agreement-text">
        {{ t("auth.agreePrefix") }}
        <em role="button" tabindex="0" @click.stop="openPrivacyPolicy"
          >《{{ t("auth.privacyPolicy") }}》</em
        >
        {{ t("auth.and") }}
        <em role="button" tabindex="0" @click.stop="openUserAgreement"
          >《{{ t("auth.userAgreement") }}》</em
        >
      </p>
    </section>

    <van-popup
      v-model:show="showLanguagePopup"
      position="bottom"
      round
      class="login-language-popup"
      overlay-class="login-language-overlay"
    >
      <section class="login-language-sheet">
        <header class="login-language-sheet__header">
          <span aria-hidden="true" />
          <h2>{{ t("mine.selectLanguage") }}</h2>
          <button
            type="button"
            :aria-label="t('common.close')"
            @click="showLanguagePopup = false"
          >
            <app-image class="app-cancel-icon" :src="closeIcon" alt="" />
          </button>
        </header>

        <div class="login-language-sheet__list">
          <button
            v-for="item in languageOptions"
            :key="item.key"
            type="button"
            class="login-language-sheet__option"
            :class="{
              'login-language-sheet__option--active':
                item.key === currentLocale,
            }"
            @click="selectLanguage(item.key)"
          >
            <span>{{ item.label }}</span>
            <AppSvgIcon
              v-if="item.key === currentLocale"
              class="login-language-sheet__selected"
              name="icon"
              size="20"
              color="var(--wp-color-theme)"
            />
            <i v-else aria-hidden="true" />
          </button>
        </div>
      </section>
    </van-popup>
  </main>
</template>

<script setup>
import { ref } from "vue";
import { showFailToast } from "vant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import AuthThemeButton from "@/view/auth/components/AuthThemeButton.vue";
import i18n from "@/i18n";
import { useProfileStore } from "@/stores/profile";
import { useThemeStore } from "@/stores/theme";
import { navigateWithState } from "@/utils/route-state";
import img1 from "@/assets/images/SVG/img1.png";
import {
  APP_LANGUAGE_OPTIONS,
  applyAppLocale,
  resolveLocale,
} from "@/utils/locale";
import { useWebModalStore } from "@/stores/web-modal";
import closeIcon from "@/assets/images/cancel_gray.png";

const PRIVACY_POLICY_URLS = {
  "zh-CN": "https://winpay.com/privacy_policy_zh-CN.html",
  "en-US": "https://winpay.com/privacy_policy_en-US.html",
};
const USER_AGREEMENT_URLS = {
  "zh-CN": "https://api.winpay.dev/user_agreement_zh-CN.html",
  "en-US": "https://winpay.com/user_agreement_en-US.html",
};

const { t, locale } = useI18n();
const router = useRouter();
const profileStore = useProfileStore();
const webModalStore = useWebModalStore();
const themeStore = useThemeStore();
const agreed = ref(false);
const agreementShaking = ref(false);
const showLanguagePopup = ref(false);
const languageOptions = computed(() => APP_LANGUAGE_OPTIONS);
const currentLocale = computed(() =>
  resolveLocale(locale.value || profileStore.locale || "zh-CN"),
);
const isZhLocale = computed(() =>
  String(locale.value || "")
    .toLowerCase()
    .startsWith("zh"),
);
const privacyPolicyUrl = computed(() =>
  themeStore.url?.[`privacy_policy_${isZhLocale.value ? "zh-CN" : "en-US"}`]
    || (isZhLocale.value
      ? PRIVACY_POLICY_URLS["zh-CN"]
      : PRIVACY_POLICY_URLS["en-US"]),
);
const userAgreementUrl = computed(() =>
  themeStore.url?.[`user_agreement_${isZhLocale.value ? "zh-CN" : "en-US"}`]
    || (isZhLocale.value
      ? USER_AGREEMENT_URLS["zh-CN"]
      : USER_AGREEMENT_URLS["en-US"]),
);

function selectLanguage(key) {
  const nextLocale = applyAppLocale(i18n, key);
  profileStore.setLocale(nextLocale);
  showLanguagePopup.value = false;
}

function openPrivacyPolicy() {
  webModalStore.open({
    url: privacyPolicyUrl.value,
    title: t("auth.privacyPolicy"),
    navBackgroundColor: "#fff",
  });
}

function openUserAgreement() {
  webModalStore.open({
    url: userAgreementUrl.value,
    title: t("auth.userAgreement"),
    navBackgroundColor: "#fff",
  });
}

function triggerAgreementWarning() {
  agreementShaking.value = false;
  requestAnimationFrame(() => {
    agreementShaking.value = true;
  });
}

function goCreateOption(path, type = "") {
  if (!agreed.value) {
    triggerAgreementWarning();
    return;
  }
  navigateWithState(router, path, {
    namespace: "auth-email",
    payload: type ? { type } : {},
  });
}

function goExistingOption(path, type = "") {
  if (!agreed.value) {
    triggerAgreementWarning();
    return;
  }
  navigateWithState(router, path, {
    namespace: "auth-email",
    payload: type ? { type } : {},
  });
}
</script>

<style scoped lang="scss">
.login-page {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: var(--app-viewport-height);
  padding: 0 14px calc(28px + env(safe-area-inset-bottom));
  background: #fff;
}

.login-shortcuts {
  position: absolute;
  top: calc(14px + env(safe-area-inset-top));
  right: 14px;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 6px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
  }

  .app-image,
  .app-svg-icon {
    display: block;
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
}

:global(.login-language-popup.van-popup--bottom) {
  left: var(--app-shell-left);
  right: auto;
  width: var(--app-shell-width);
  max-width: 100vw;
  overflow: hidden;
  background: #ffffff;
}

:global(.login-language-overlay) {
  left: var(--app-shell-left);
  right: auto;
  width: var(--app-shell-width);
  max-width: 100vw;
}

.login-language-sheet {
  padding: 0 14px calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
}

.login-language-sheet__header {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr) 28px;
  align-items: center;
  min-height: 70px;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 20px;
    font-weight: 500;
    line-height: 28px;
    text-align: center;
  }

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: 0;
    background: transparent;

    .app-image {
      width: 20px;
      height: 20px;
      object-fit: contain;
    }
  }
}

.login-language-sheet__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.login-language-sheet__option {
  display: flex;
  width: 100%;
  height: 52px;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  border: 1px solid #c1c4cd;
  border-radius: 10px;
  background: #ffffff;
  color: #222222;
  font-size: 16px;
  line-height: 22px;
  text-align: left;

  > i,
  .login-language-sheet__selected {
    width: 20px;
    height: 20px;
    flex: 0 0 20px;
  }

  > i {
    border: 1px solid var(--wp-color-theme);
    border-radius: 50%;
  }
}

.login-language-sheet__option--active {
  border-color: var(--wp-color-theme);
  background: var(--wp-color-card-3);
}

.login-language-sheet__selected {
  object-fit: contain;
}

.login-hero {
  flex: 1 0 auto;
  text-align: center;

  .app-image {
    display: block;
    width: calc(100% + 28px);
    max-width: none;
    height: auto;
    margin: 5% auto 0;
    object-fit: contain;
    object-position: center;

    :deep(.van-image__img) {
      height: auto;
      object-fit: contain;
      object-position: center;
    }
  }

  h1 {
    margin: 0;
    color: #000000;
    font-size: 28px;
    font-weight: 700;
    line-height: 36px;
  }

  p {
    margin: 6px 0 0;
    color: rgba(0, 0, 0, 0.65);
    font-size: 14px;
    line-height: 20px;
  }
}

.login-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 38px;
}

.agreement-row {
  display: flex;
  align-items: flex-start;
  margin-top: 12px;
}

.agreement-row--shaking {
  animation: agreement-shake 360ms ease-in-out;
}

.agreement-check {
  flex: 0 0 auto;
  padding: 10px 2px 10px 0;
  border: none;
  background: transparent;
  cursor: pointer;

  .app-image {
    display: block;
    width: 16px;
    height: 16px;
  }

  .app-svg-icon {
    display: block;
    vertical-align: initial;
  }
}

.agreement-text {
  flex: 1;
  margin: 0;
  padding: 10px 0 0;
  color: #333333;
  font-size: 12px;
  line-height: 18px;

  em {
    color: var(--wp-color-theme);
    font-style: normal;
    cursor: pointer;
  }
}

.wallet-sheet {
  padding: 28px 14px calc(42px + env(safe-area-inset-bottom));
  background: #ffffff;

  h2 {
    margin: 0;
    color: #000000;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
    text-align: center;
  }

  > p {
    margin: 10px 0 28px;
    color: #999999;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
}

.wallet-sheet__item {
  display: flex;
  width: 100%;
  align-items: center;
  padding: 15px 16px;
  border: 1px solid var(--wp-color-card-3);
  border-radius: 16px;
  background: #f6fafd;
  text-align: left;

  & + & {
    margin-top: 14px;
  }

  > div:last-of-type {
    flex: 1;
    min-width: 0;
  }

  :deep(.van-icon) {
    color: #999999;
    font-size: 18px;
    flex-shrink: 0;
  }
}

.wallet-sheet__item--primary {
  border-color: transparent;
  background: linear-gradient(90deg, #378cfd 0%, var(--wp-color-theme) 100%);

  :deep(.van-icon) {
    color: #ffffff;
  }

  [data-strong],
  span {
    color: #ffffff !important;
  }
}

.wallet-sheet__icon {
  display: flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  flex-shrink: 0;

  .app-image {
    width: 42px;
    height: 42px;
    object-fit: contain;
  }
}

@keyframes agreement-shake {
  0%,
  100% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-4px);
  }
  40% {
    transform: translateX(4px);
  }
  60% {
    transform: translateX(-3px);
  }
  80% {
    transform: translateX(3px);
  }
}

.wallet-sheet__item > div:last-of-type {
  [data-strong],
  span {
    display: block;
  }

  [data-strong] {
    color: #333333;
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
  }

  span {
    margin-top: 6px;
    color: #878b99;
    font-size: 12px;
    line-height: 18px;
  }
}
</style>
